"use client"

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@workspace/web-ui/components/accordion"
// import { Avatar, AvatarFallback, AvatarImage } from "@workspace/web-ui/components/avatar"
import { Badge } from "@workspace/web-ui/components/badge"
import { Button } from "@workspace/web-ui/components/button"
import { Card, CardContent } from "@workspace/web-ui/components/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@workspace/web-ui/components/tabs"
import { Calendar, Check, Circle, CircleChevronDown, DollarSign, FileText } from "lucide-react"
import { useState } from "react"
import AlfieImage from "~/assets/alfie-logo.svg"
import {AlfieChatBot} from "../../components/alfie-chat"
import { useRouter } from "@tanstack/react-router"
import { useDiagnosisStore } from "../../store/use-diagnosis-store"
import { useSuspenseQuery } from "@tanstack/react-query"
import { sessionsQueryOptions } from "../../api/queries/session"
// import { useChatMutation } from "../../api/mutations/chat"

export default function UrgentCarePage() {
  const [showChatBot, setShowChatBot] = useState(false)
  const sessionQuery = useSuspenseQuery(sessionsQueryOptions());
  // const [activeItem, setActiveItem] = useState("")
  const [activeTodoTask, setActiveTodoTask] = useState("")
  const [activeCompletedTask, setActiveCompletedTask] = useState("")
  const [selectedSessionId, setSelectedSessionId] = useState<string | undefined>(undefined)
  const {diagnosisData, consentChecked} = useDiagnosisStore()
  // const [initialChatMessage, setInitialChatMessage] = useState("")
  // const [initialThreadId, setInitialThreadId] = useState("")
  // // const [errorMessage, setErrorMessage] = useState("")
  // const { mutate: creatChatMutation } = useChatMutation();
  const router = useRouter()

  console.log("session", sessionQuery?.data?.sessions)

  // Mock user data
  const user = {
    first_name: "Maurice",
    last_name: "Smith",
    avatar: "",
    health_profile: {
      weight: 185,
      target_weight: 165,
      timeframe: "3 months",
    },
  }

  // Mock task data
  const urgentCareTasks = {
    month: "May",
    data: [
      ...(diagnosisData ? [] : [{
        id: "1",
        title: "Get diagnosed with Alfie",
        type: "Consultation" as const,
        completed: false,
        priority: true,
        when: "2025-05-23",
        payment_required: false,
      }]),
      ...(diagnosisData ? [{
        id: "2",
        title: "Book a follow up appointment with a Doctor (Optional)",
        type: "Appointment" as const,
        completed: false,
        priority: false,
        when: "2025-05-23",
        payment_required: true,
      }] : []),
      ...(diagnosisData && !consentChecked ? [{
        id: "3",
        title: "Pay for consultation",
        type: "Consultation" as const,
        completed: false,
        priority: true,
        when: "2025-05-24",
        payment_required: true,
        consultation_id: "cons-123",
      }] : []),
      // {
      //   id: "4",
      //   title: "Join appointment",
      //   type: "Appointment" as const,
      //   completed: false,
      //   priority: true,
      //   when: "2025-05-25",
      //   payment_required: false,
      // },
    ],
  }

  // Calculate potential completed tasks from mock data for the badge, excluding the initial "Get diagnosed" task if diagnosisData exists.
  const mockCompletedCount = urgentCareTasks.data.filter((task: Task) => task.completed).length;

  const completedStages = [urgentCareTasks.data.every((task: Task) => task.completed)]

  interface Task {
    id: string
    title: string
    type: "Consultation" | "Appointment"
    completed: boolean
    priority: boolean
    when: string
    payment_required: boolean
    consultation_id?: string
  }

  const handleChatBotOpen = (sessionId?: string) => {
    setShowChatBot(true)
    setSelectedSessionId(sessionId)
  }

  const renderTaskActionButton = (task: Task): React.ReactNode => {
    // Talk with Alfie
    if (task.type === "Consultation" && !task.payment_required && !task.completed) {
      return (
        <Button onClick={() => handleChatBotOpen(task.id.split('-')[1])} className="w-full sm:w-auto rounded-lg bg-[#4272DD]">
          Talk to Alfie
        </Button>
      )
    }

    // Pay for consultation
    if (task.type === "Consultation" && task.payment_required) {
      return (
        <Button 
        className="w-full sm:w-auto rounded-lg bg-[#4272DD]"
        onClick={() => {
          router.navigate({ to: `/consultations`, });
        }}
        >Make payment</Button>
      )
    }

    if (task.type === "Appointment" && task.payment_required) {
      return <Button 
      onClick={() => {
        router.navigate({ to: `/dashboard/urgent-care/appointments/booking`, });
      }}
      className="w-full sm:w-auto rounded-lg bg-[#4272DD]">Book appointment</Button>
    }

    // Join appointment
    if (task.type === "Appointment" && !task.payment_required && !task.completed) {
      return <Button className="w-full sm:w-auto rounded-lg bg-[#4272DD]">Join appointment</Button>
    }

    return null
  }

  // interface AccordionContentDetails {
  //   text: string;
  //   value: string;
  // }

  // interface TaskDetails {
  //   date?: string;
  //   amount?: string;
  //   consultationType?: string;
  // }

  const renderAccordionContent = (task: Task): React.ReactNode => {
    if (task.completed) {
      // Content for completed tasks
      return (
        <div className="space-y-4 w-full">
          <div className="space-y-2">
            <h3 className="text-base font-medium">What to expect next</h3>
            <ol className="list-decimal pl-5 space-y-1">
              {task.type === "Appointment" && (
                <>
                  <li className="text-sm text-gray-700">
                    Review and implement the discussed recommendations or action items.
                  </li>
                  <li className="text-sm text-gray-700">
                    You will receive notifications and updates on your dashboard.
                  </li>
                  <li className="text-sm text-gray-700">
                    You can contact support if you have any questions or concerns.
                  </li>
                </>
              )}
              {task.type === "Consultation" && (
                <>
                  <li className="text-sm text-gray-700">
                    Our medical team will review your case and create a personalized plan.
                  </li>
                  <li className="text-sm text-gray-700">
                    You will receive notifications and updates on your dashboard.
                  </li>
                  <li className="text-sm text-gray-700">
                    You can contact support if you have any questions or concerns.
                  </li>
                </>
              )}
            </ol>
          </div>

          {/* Display additional information for completed tasks */}
          {(task.type === "Consultation" || task.type === "Appointment") && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
              <div className="flex items-start gap-2">
                <Calendar className="size-5 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500">Date initiated</p>
                  <p className="text-sm font-medium">{task.when || "24 April, 2025"}</p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <DollarSign className="size-5 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500">Amount Paid:</p>
                  <p className="text-sm font-medium">{"$50.00"}</p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <FileText className="size-5 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500">
                    {task.type === "Consultation" || task.type === "Appointment"
                      ? "Consultation Details:"
                      : "Medications"}
                  </p>
                  <p className="text-sm font-medium">
                    {task.type === "Consultation" || task.type === "Appointment"
                      ? "Urgent Care Consultation"
                      : "Prescribed Medications"}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )
    } else {
      // Content for pending tasks
      return (
        <div className="space-y-4 w-full">
          <div className="space-y-2">
            <h3 className="text-base font-medium">What to do</h3>
            <ol className="list-decimal pl-5 space-y-1">
              {task.type === "Consultation" && !task.payment_required && (
                <>
                  <li className="text-sm text-gray-700">
                  Click the Start Diagnosis button to begin chatting with Alfie.
                  </li>
                  <li className="text-sm text-gray-700">Answer a few short questions about your symptoms and health history</li>
                  <li className="text-sm text-gray-700">Alfie will review your responses and either offer a possible diagnosis or recommend next steps.</li>
                </>
              )}
              {task.type === "Consultation" && task.payment_required && (
                <>
                  <li className="text-sm text-gray-700">Pay now to start your consultation.</li>
                  <li className="text-sm text-gray-700">
                    Our team will review your case and create a personalized plan.
                  </li>
                  <li className="text-sm text-gray-700">You'll receive updates on your dashboard.</li>
                </>
              )}
              {task.type === "Appointment" && (
                <>
                  <li className="text-sm text-gray-700">
                    Review any provided materials and come prepared with questions or topics to discuss
                  </li>
                  <li className="text-sm text-gray-700">
                    Click the meeting link or dial-in number to join at the scheduled time
                  </li>
                </>
              )}
            </ol>
          </div>

          <div className="pt-2">
            <p className="text-sm text-gray-700">
              Contact Support: Need help? Reach out to our support team appointments@proxymedicine.com
            </p>
          </div>
        </div>
      )
    }
  }

  return (
    <main className="flex flex-col w-full gap-6">
      <section className="py-10">
        {/* Greetings */}
        <span className="inline-flex gap-2 pb-4">
          <h6 className="text-lg font-normal">Welcome,</h6>
          <h6 className="text-lg font-bold">{user?.first_name}</h6>
        </span>

        {/* Subnav one */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <h1 className="text-2xl font-bold mb-4 sm:mb-0">Urgent Care</h1>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Button variant="outline" className="border-[#DCDCDE] py-[16px] px-[12px] rounded-[8px] text-[#202020CC]" onClick={() => handleChatBotOpen()}>
              <img src={AlfieImage} alt="alfie" className="h-5 w-5" />
              Start a treatment
            </Button>
          </div>
        </div>


          {/* Timeline */}
          <div className="space-y-8">
          {sessionQuery?.data?.sessions && sessionQuery.data.sessions.length > 0 ? (
            // Existing sessions logic
            sessionQuery.data.sessions.map((session, sessionIndex) => (
              <div key={session.session_id} className="flex flex-col sm:flex-row">
                {/* Timeline Indicator - Hidden on mobile */}
                <div className="relative hidden w-10 flex-col items-center sm:flex">
                  {completedStages[sessionIndex] ? (
                    <Circle className="h-6 w-6 text-green-500" />
                  ) : (
                    <>
                      <div className="relative flex size-10 items-center justify-center">
                        <Circle
                          className={`absolute size-7 ${
                            sessionIndex === 0 || completedStages[sessionIndex - 1] ? "text-[#4272DD]" : "text-[#DCDCDE]"
                          }`}
                        />
                        <Circle
                          strokeWidth={5}
                          className={`absolute size-3 ${
                            sessionIndex === 0 || completedStages[sessionIndex - 1] ? "text-[#4272DD]" : "text-[#DCDCDE]"
                          }`}
                        />
                      </div>

                      <div
                        className={`-mt-2 h-full w-1 ${sessionIndex === 0 ? "bg-gradient-to-b from-blue-500 to-transparent" : "bg-gradient-to-b from-gray-300 to-transparent opacity-40"}`}
                      ></div>
                    </>
                  )}
                  {sessionIndex < urgentCareTasks.data.length - 1 && (
                    <div
                      className={`-mt-2 h-full w-1 ${
                        sessionIndex === 0
                          ? "bg-gradient-to-b from-[#4272DD] to-transparent"
                          : "bg-gradient-to-b from-[#DCDCDE] to-transparent opacity-40"
                      }`}
                    ></div>
                  )}
                </div>

                {/* Card Section */}
                <Card className="w-full rounded-[24px]">
                  <CardContent className="p-0 rounded-[24px]">
                    <div className="flex items-start justify-between gap-2 border-b px-6 pb-4 pt-3 sm:flex-row sm:items-center">
                      <h2 className="text-lg font-semibold">
                        {session 
                          ? `# Session ${session.session_number}`
                          : "# Session 1"}
                      </h2>
                      <Badge
                        className={`rounded-2xl text-sm hover:bg-[#E1FAEA] ${
                          completedStages[sessionIndex] ? "bg-[#E1FAEA] text-[#14AD4D]" : "bg-[#FFF1DA] text-[#9D6508]"
                        }`}
                      >
                        {completedStages[sessionIndex] ? "Completed" : "Ongoing"}
                      </Badge>
                    </div>

                    {/* Tabs */}
                    <Tabs defaultValue="to-do">
                      <TabsList className="flex h-12 w-full flex-wrap justify-start gap-4 rounded-none border-b bg-background px-6 py-0">
                        <TabsTrigger
                          value="to-do"
                          className="h-full rounded-none border-b-2 border-transparent bg-background px-0 py-4 text-sm data-[state=active]:border-primary data-[state=active]:shadow-none"
                        >
                          To-do
                          <Badge className="ml-2 size-4 items-center justify-center rounded-full bg-[#FFAB1F] p-1 text-center text-xs">
                            {/* Calculate To-do tasks based on session state */}
                            {session.alfie_session === 'active' ? 1 : 0}
                          </Badge>
                        </TabsTrigger>
                        <TabsTrigger
                          value="completed"
                          className="h-full rounded-none border-b-2 border-transparent bg-background px-0 text-sm data-[state=active]:border-primary data-[state=active]:shadow-none"
                        >
                          Completed
                          <Badge className="ml-2 size-4 items-center justify-center rounded-full bg-[#FFAB1F] p-1 text-center text-xs">
                            {/* Calculate Completed tasks based on session state */}
                            {mockCompletedCount + (session.alfie_session !== 'active' ? 1 : 0)}
                          </Badge>
                        </TabsTrigger>
                      </TabsList>

                      {/* To-do Tasks */}
                      <TabsContent value="to-do" className="px-6">
                        {/* Render tasks dynamically based on session.alfie_session */}
                        <Accordion
                          type="single"
                          collapsible
                          value={activeTodoTask}
                          onValueChange={setActiveTodoTask}
                          className="w-full"
                        >
                          {(() => {
                            const sessionTasks = [];
                            if (session.alfie_session === 'active') {
                              sessionTasks.push({
                                id: `session-${session.session_id}-continue-chat`,
                                title: "Continue diagnosis with Alfie",
                                type: "Consultation" as const, // Treat as Consultation for action button
                                completed: false,
                                priority: true, // Assuming continuing chat is high priority
                                when: "Now", // Or fetch actual date if available
                                payment_required: false, // Continuing chat is free
                              });
                            }

                            // Only render the Accordion if there are tasks to show in To-do
                            if (sessionTasks.length === 0) {
                              return (
                                <div className="flex flex-col items-center justify-center py-8">
                                  <div className="rounded-full bg-gray-100 p-3 mb-4">
                                    <FileText className="h-6 w-6 text-gray-400" />
                                  </div>
                                  <h3 className="text-xl font-medium mb-2">No pending tasks</h3>
                                  <p className="text-gray-500 text-center max-w-md">
                                    Great job! All tasks for this session are completed or the next step is not yet available.
                                  </p>
                                </div>
                              );
                            }

                            return sessionTasks.map((task) => (
                              <AccordionItem
                                key={task.id}
                                value={task.id}
                                className="my-4 border rounded-[20px] shadow-sm"
                              >
                                <AccordionTrigger className="px-4 py-3 relative hover:no-underline [&>svg:last-child]:hidden">
                                  <div className="flex w-full flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-2">
                                    <div className="flex items-start gap-3 flex-1">
                                      <Circle
                                        strokeWidth={2}
                                        className="size-6 shrink-0 text-[#4272DD] mt-1 sm:mt-0"
                                      />
                                      <div className="flex flex-col text-left">
                                        <h3 className="text-base font-medium break-words">{task.title}</h3>
                                        {task.when && <p className="text-sm text-gray-500">{task.when}</p>}
                                      </div>
                                      {task.priority && (
                                        <Badge className="h-6 w-16 hidden sm:flex rounded-3xl bg-[#FFF1DA] text-[#9D6508] hover:bg-[#FFF1DA]">
                                          Priority
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="flex w-full sm:w-auto justify-start sm:justify-end">
                                      {task.type === "Consultation" && !task.payment_required && !task.completed ? (
                                        <Button onClick={() => handleChatBotOpen(task.id.split('-')[1])} className="w-full sm:w-auto rounded-lg bg-[#4272DD]">
                                          Resume Diagnosis
                                        </Button>
                                      ) : (
                                        renderTaskActionButton(task)
                                      )}
                                    </div>
                                  </div>
                                  <CircleChevronDown
                                    strokeWidth={0.75}
                                    className="size-8 top-0 mt-3 md:mt-0 md:ml-2 right-0 mr-2 md:mr-0 text-[#7F8493] absolute md:relative transition-transform duration-300 data-[state=open]:rotate-180"
                                  />
                                </AccordionTrigger>
                                <AccordionContent className="px-4 py-2">
                                  {/* Content for the task */}
                                  {renderAccordionContent(task)} {/* Use the existing function */}
                                </AccordionContent>
                              </AccordionItem>
                            ));
                          })()}
                        </Accordion>
                      </TabsContent>

                      {/* Completed Tasks */}
                      <TabsContent value="completed" className="px-6">
                        {/* Render completed tasks dynamically */}
                        {(() => {
                          const completedTasks = urgentCareTasks.data.filter((task: Task) => task.completed);
                          // Add "Diagnosis with Alfie completed" task if session is not active
                          if (session.alfie_session !== 'active') {
                            completedTasks.push({
                              id: `session-${session.session_id}-alfie-completed`,
                              title: "Diagnosis with Alfie completed",
                              type: "Consultation" as const,
                              completed: true,
                              priority: false, // Or true, depending on desired visual emphasis
                              when: "Recently", // Placeholder, ideally use a real completed date
                              payment_required: false,
                            });
                          }

                          if (completedTasks.length > 0) {
                            return (
                              <Accordion
                                type="single"
                                collapsible
                                value={activeCompletedTask}
                                onValueChange={setActiveCompletedTask}
                                className="w-full"
                              >
                                {completedTasks.map((task: Task, index: number) => (
                                  <AccordionItem
                                    key={`completed-${index}`}
                                    value={`completed-${index}`}
                                    className="my-4 border rounded-[20px] shadow-sm bg-[#EBFBF1]"
                                  >
                                    <AccordionTrigger className="px-4 py-3 hover:no-underline [&>svg:last-child]:hidden">
                                      <div className="flex w-full justify-between items-center">
                                        <div className="flex items-start gap-3">
                                          <div className="flex size-6 items-center justify-center rounded-full bg-[#14AD4D]">
                                            <Check className="size-3 text-white" strokeWidth={3} />
                                          </div>
                                          <div className="flex flex-col text-left">
                                            <h3 className="text-base font-medium">{task.title}</h3>
                                            <p className="text-sm text-gray-500">Completed</p>
                                          </div>
                                        </div>
                                      </div>
                                      <CircleChevronDown
                                        strokeWidth={0.75}
                                        className="size-8 text-[#7F8493] transition-transform duration-300 data-[state=open]:rotate-180"
                                      />
                                    </AccordionTrigger>
                                    <AccordionContent className="px-4 py-2">
                                      <div className="flex mt-2">{renderAccordionContent(task)}</div>
                                    </AccordionContent>
                                  </AccordionItem>
                                ))}
                              </Accordion>
                            );
                          } else {
                            return (
                              <div className="flex flex-col items-center justify-center py-8">
                                <div className="rounded-full bg-gray-100 p-3 mb-4">
                                  <FileText className="h-6 w-6 text-gray-400" />
                                </div>
                                <h3 className="text-xl font-medium mb-2">No completed tasks</h3>
                                <p className="text-gray-500 text-center max-w-md">
                                  You haven't completed any urgent care tasks yet. Check the to-do tab to see what needs to be
                                  done.
                                </p>
                              </div>
                            );
                          }
                        })()}
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              </div>
            ))
          ) : (
            // Default session when no sessions exist
            <div className="flex flex-col sm:flex-row">
              {/* Timeline Indicator - Hidden on mobile */}
              <div className="relative hidden w-10 flex-col items-center sm:flex">
                <div className="relative flex size-10 items-center justify-center">
                  <Circle className="absolute size-7 text-[#4272DD]" />
                  <Circle strokeWidth={5} className="absolute size-3 text-[#4272DD]" />
                </div>
                <div className="bg-gradient-to-b from-[#4272DD] to-transparent -mt-2 h-full w-1"></div>
              </div>

              {/* Card Section */}
              <Card className="w-full rounded-[24px]">
                <CardContent className="p-0 rounded-[24px]">
                  <div className="flex items-start justify-between gap-2 border-b px-6 pb-4 pt-3 sm:flex-row sm:items-center">
                    <h2 className="text-lg font-semibold"># Session 1</h2>
                    <Badge className="rounded-2xl text-sm bg-[#FFF1DA] text-[#9D6508] hover:bg-[#FFF1DA]">
                      Ongoing
                    </Badge>
                  </div>

                  {/* Tabs */}
                  <Tabs defaultValue="to-do">
                    <TabsList className="flex h-12 w-full flex-wrap justify-start gap-4 rounded-none border-b bg-background px-6 py-0">
                      <TabsTrigger
                        value="to-do"
                        className="h-full rounded-none border-b-2 border-transparent bg-background px-0 py-4 text-sm data-[state=active]:border-primary data-[state=active]:shadow-none"
                      >
                        To-do
                        <Badge className="ml-2 size-4 items-center justify-center rounded-full bg-[#FFAB1F] p-1 text-center text-xs">
                          {/* Default session has 1 to-do initially */}
                          1
                        </Badge>
                      </TabsTrigger>
                      <TabsTrigger
                        value="completed"
                        className="h-full rounded-none border-b-2 border-transparent bg-background px-0 text-sm data-[state=active]:border-primary data-[state=active]:shadow-none"
                      >
                        Completed
                        <Badge className="ml-2 size-4 items-center justify-center rounded-full bg-[#FFAB1F] p-1 text-center text-xs">
                          {/* Default session has 0 completed initially */}
                          {urgentCareTasks.data.filter((task) => task.completed).length}
                        </Badge>
                      </TabsTrigger>
                    </TabsList>

                    {/* To-do Tasks */}
                    <TabsContent value="to-do" className="px-6">
                      {/* Render tasks dynamically based on session.alfie_session */}
                      <Accordion
                        type="single"
                        collapsible
                        value={activeTodoTask}
                        onValueChange={setActiveTodoTask}
                        className="w-full"
                      >
                        {/* Directly map over urgentCareTasks.data for the default session */}
                        {[
                          {
                            id: "1",
                            title: "Get diagnosed with Alfie",
                            type: "Consultation" as const,
                            completed: false,
                            priority: true,
                            when: "Now", // Or fetch actual date if available
                            payment_required: false,
                          },
                        ].map((task) => ( // Directly map over urgentCareTasks.data for the default session
                          <AccordionItem
                            key={task.id}
                            value={task.id}
                            className="my-4 border rounded-[20px] shadow-sm"
                          >
                            <AccordionTrigger className="px-4 py-3 relative hover:no-underline [&>svg:last-child]:hidden">
                              <div className="flex w-full flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-2">
                                <div className="flex items-start gap-3 flex-1">
                                  <Circle
                                    strokeWidth={2}
                                    className="size-6 shrink-0 text-[#4272DD] mt-1 sm:mt-0"
                                  />
                                  <div className="flex flex-col text-left">
                                    <h3 className="text-base font-medium break-words">{task.title}</h3>
                                    {task.when && <p className="text-sm text-gray-500">{task.when}</p>}
                                  </div>
                                  {task.priority && (
                                    <Badge className="h-6 w-16 hidden sm:flex rounded-3xl bg-[#FFF1DA] text-[#9D6508] hover:bg-[#FFF1DA]">
                                      Priority
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex w-full sm:w-auto justify-start sm:justify-end">
                                  {task.type === "Consultation" && !task.payment_required && !task.completed ? (
                                    <Button onClick={() => handleChatBotOpen(task.id.split('-')[1])} className="w-full sm:w-auto rounded-lg bg-[#4272DD]">
                                      Talk to Alfie
                                    </Button>
                                  ) : (
                                    renderTaskActionButton(task)
                                  )}
                                </div>
                              </div>
                              <CircleChevronDown
                                strokeWidth={0.75}
                                className="size-8 top-0 mt-3 md:mt-0 md:ml-2 right-0 mr-2 md:mr-0 text-[#7F8493] absolute md:relative transition-transform duration-300 data-[state=open]:rotate-180"
                              />
                            </AccordionTrigger>
                            <AccordionContent className="px-4 py-2">
                              {/* Content for the task */}
                              {renderAccordionContent(task)} {/* Use the existing function */}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </TabsContent>

                    {/* Completed Tasks */}
                    <TabsContent value="completed" className="px-6">
                        <div className="flex flex-col items-center justify-center py-8">
                          <div className="rounded-full bg-gray-100 p-3 mb-4">
                            <img src={AlfieImage} alt="alfie" className="h-6 w-6" />
                          </div>
                          <h3 className="text-xl font-medium mb-2">Ready to start?</h3>
                          <p className="text-gray-500 text-center max-w-md mb-4">
                            Begin your urgent care journey by talking to Alfie for an initial diagnosis.
                          </p>
                          <Button onClick={() => handleChatBotOpen()} className="rounded-lg bg-[#4272DD]">
                            <img src={AlfieImage} alt="alfie" className="h-4 w-4 mr-2" />
                            Talk to Alfie
                          </Button>
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </section>

      {/* Chatbot using Sheet component */}
      <AlfieChatBot open={showChatBot} onOpenChange={setShowChatBot} sessionId={selectedSessionId} />
    </main>
  )
}
